import { Component, ElementRef, Inject, ViewChild } from '@angular/core';
import { NgProgress } from '@ngx-progressbar/core';
import { SelectComponent } from 'ng2-select';
import { ToastrService } from 'ngx-toastr';
import { AssetBaseComponent } from '../../../modules/asset/components/asset-base.component';
import { AssetModel } from '../../../modules/asset/components/model/asset.model';
import { MakeModel } from '../../../modules/make/model/make.model';
import { ProductSearchModel } from '../../../modules/product/model/product-search.model';
import { ProductUIMessages } from '../../../modules/product/model/product-ui.messages';
import { ProductModel } from '../../../modules/product/model/product.model';
import { ProductService } from '../../../modules/product/service/product.service';
import { AuthService } from '../../../modules/security/service/auth.service';
import { NgSelectOptionModel } from '../../model/ng-select-option.model';
import { UIActiveDialogService } from '../../services/ui-dialog.service';
import {
  KEYSTROKE,
  PREVENT_KEYUP_EVENTS,
  SEARCH_QUITE_PERIOD
} from '../../utils/miscellaneous.utils';
import { SortUtils } from '../../utils/sort.utils';
import { APIOperableComponent } from '../api-operable/api-operable.component';
import { ListeableComponent } from '../interfaces/listeable.component';

@Component({
  selector: 'product-selector-dialog',
  templateUrl:
    './console/app-js/shared/components/product-selector-dialog/product-selector-dialog.component.html'
})
export class ProductSelectorDialogComponent extends APIOperableComponent
  implements ListeableComponent {
  @ViewChild('makeModelSelect', { static: true })
  protected makeModelSelect: SelectComponent;
  @ViewChild('searchInput', { static: true }) searchInput: ElementRef;
  columns = [
    {
      label: 'model',
      property: 'model',
      type: 'string'
    }
  ];
  protected searchModel = new ProductSearchModel();
  protected products: Array<ProductModel> = [];
  protected productSelected: Array<any> = [];
  protected operations: any = {
    loadingProducts: false
  };
  protected options: any = {
    linesOfService: [],
    makes: [],
    makeModel: []
  };
  noRecordsSettings;
  protected isStarbucksSite = false;
  private productMakes: Array<MakeModel> = [];
  private typingTimeout: any;
  private assets: Array<AssetModel>;
  private originalData: Array<ProductModel>;
  private hasNextPage: Boolean = false;
  protected filterValue: string;
  protected filteredMakes: Array<MakeModel>;
  protected makeFilterSelected: MakeModel;
  customization: any;

  constructor(
    @Inject('product') product: ProductModel,
    @Inject('isStarbucksSite') isStarbucksSite: boolean,
    @Inject('assets') assets: Array<AssetModel>,
    @Inject('customization') customization: any,
    progressService: NgProgress,
    toastr: ToastrService,
    authService: AuthService,
    private productService: ProductService,
    private activeDialog: UIActiveDialogService
  ) {
    super(progressService, toastr, authService);
    this.isStarbucksSite = isStarbucksSite;
    this.filteredMakes = [];
    this.onLoad(product);
    this.assets = assets;
    this.originalData = [];
    this.filterValue = '';
    this.customization = customization ? customization : {};
    this.setDefaultNoRecordSettings();
  }

  /**
   * Load all lists from API for the view.
   * @param {ProductModel} product
   */
  private onLoad(product: ProductModel): void {
    this.searchModel.filterModel.type = 'Machine';
    this.searchModel.pageSize = 100;
    this.productSelected.push({ ...product });
    this.loadProductMakes();
  }

  /**
   * Just verify if the isStarbucksSite flag is activated.
   */
  private isStarbucksSiteFlagActivated(): boolean {
    if (this.isStarbucksSite) {
      let defaultMakeModelSelection: NgSelectOptionModel = {
        id: AssetBaseComponent.STARBUCKS_MAKE_MODEL_LIST[0],
        text: AssetBaseComponent.STARBUCKS_MAKE_MODEL_LIST[0]
      };
      this.makeModelSelect.active = [defaultMakeModelSelection];
      this.onMakeModelSelected(defaultMakeModelSelection);
      return true;
    }
    return false;
  }

  /**
   * Gets the static Make Model List for Starbucks site.
   * @returns {Array<any>}
   */
  protected getMakeModelList(): Array<any> {
    return AssetBaseComponent.STARBUCKS_MAKE_MODEL_LIST;
  }

  /**
   * On MakeModel selected option.
   * @param {NgSelectOptionModel} $event
   */
  protected onMakeModelSelected($event: NgSelectOptionModel): void {
    let makeModelSelection: Array<string> = $event.text.split(' / ');
    this.searchModel.filterModel.make = makeModelSelection[0];
    this.searchModel.filterModel.model = makeModelSelection[1];
    this.onSearch();
  }

  /**
   * On Make list selected option.
   * @param {NgSelectOptionModel} $event
   */
  protected onMakeSelected($event: NgSelectOptionModel): void {
    this.searchModel.filterModel.make = $event.text;
    this.onSearch();
  }

  /**
   * On Make list removed option.
   * @param {NgSelectOptionModel} $event
   */
  protected onMakeRemoved(): void {
    this.searchModel.filterModel.make = '';
    this.onSearch();
  }

  /**
   * On Model input clear option.
   * @param {NgSelectOptionModel} $event
   */
  protected onModelRemoved(): void {
    this.searchModel.filterModel.model = '';
    this.onSearch();
  }

  onSearch(refreshGrid = false, requiresAudit = true): void {
    // this.progressRef.start();

    this.loadingIndicator = true;
    this.operations.loadingProducts = true;
    this.setDefaultNoRecordSettings();
    if (refreshGrid) {
      this.originalData = [];
      this.hasNextPage = false;
      this.searchModel.pageNumber = 1;
    }
    if (!requiresAudit) {
      this.searchModel.filterModel.requiresAudit = 'No';
    }
    if (this.hasNextPage) {
      this.searchModel.pageNumber += 1;
    }
    this.productService.searchPartAndProduct(this.searchModel).subscribe(
      (res: any) => {
        this.searchModel = this.productService.getSearchResultModel(
          this.searchModel,
          res
        );
        if (res.responseCode === 200 && res.results) {
          this.searchModel.results = res.results;
          this.products = SortUtils.sortCollection(res.results, 'model');
          let dataCheck = JSON.parse(JSON.stringify(this.products));
          this.originalData.push(...dataCheck);
        } else {
          this.searchModel.results = [];
          this.products = [];
          this.onAPIError(
            res.errorMessage
              ? res.errorMessage
              : ProductUIMessages.SEARCH_PRODUCTS_ERROR
          );
        }
        this.products = [...this.originalData];
        this.searchAndSelectProduct();
        this.hasNextPage = res.pageNumber < res.totalPages ? true : false;
        this.operations.loadingProducts = false;
        // this.progressRef.complete();
        this.loadingIndicator = false;
        setTimeout(() => {
          this.searchInput.nativeElement.focus();
        }, 100);
      },
      () => {
        this.loadingIndicator = false;
        return this.onAPIError(ProductUIMessages.SEARCH_PRODUCTS_ERROR);
      }
    );
  }

  onReset(): void {
    this.searchModel.filterModel.name = null;
    this.onSearch();
  }

  /**
   * Set No Records Settings to Default state.
   */
  private setDefaultNoRecordSettings(): void {
    this.noRecordsSettings = {
      message: '',
      styleClass: 'no-records-wrapper'
    };
  }

  onAddItem(): void {
    this.activeDialog.close({
      product: this.productSelected[0],
      make: this.getMake(this.productSelected[0].makeId)
    });
  }

  /**
   * Loads the list of product makes.
   */
  private loadProductMakes(): void {
    this.productService.getProductMakes().subscribe(
      (res: any) => {
        if (res.responseCode === 200) {
          this.productMakes = res.results;
          this.productMakes = this.productMakes
            .sort((a, b) => SortUtils.compareByProperty(a, b, 'name'))
            .filter(make => {
              return !make.requiresAudit;
            });
          this.filteredMakes = [...this.productMakes];
        } else {
          this.onAPIError('An error ocurred while loading product makes');
        }
        if (!this.isStarbucksSiteFlagActivated()) {
          this.onSearch(false, false);
        }
      },
      error => this.onAPIError('An error ocurred while loading product makes')
    );
  }

  /**
   * Search for a product on the current data set and adds it to the current productSelection.
   */
  private searchAndSelectProduct(): void {
    if (this.productSelected[0] && this.productSelected[0].id) {
      const match = this.products.find(
        item => item.id === this.productSelected[0].id
      );
      if (match) {
        this.productSelected = [];
        this.productSelected.push(match);
      }
    }
  }

  /**
   * Get Make by id from the current list of makes previously loaded.
   * @param {number} makeId
   * @returns {MakeModel}
   */
  private getMake(makeId: number): MakeModel {
    return this.productMakes.find(item => item.id === makeId);
  }

  /**
   * KeyUp event handler. Search a product every N miliseconds after key up.
   * @param {KeyboardEvent} e
   */
  private onSearchByKeyUp(e: KeyboardEvent): void {
    if (this.preventSearch()) {
      return; // prevent search
    }
    if (e.code === KEYSTROKE.ENTER) {
      this.onSearch();
    } else if (!PREVENT_KEYUP_EVENTS.test(e.code)) {
      clearTimeout(this.typingTimeout);
      this.typingTimeout = setTimeout(
        () => this.onSearch(),
        SEARCH_QUITE_PERIOD
      );
    }
  }

  private preventSearch(): boolean {
    return false;
  }

  /**
   * On Cancel/Close button event.
   */
  private onCancelClose(): void {
    this.activeDialog.dismiss();
  }

  onFullscreenMode(): void {
    // Not supported on this component.
  }

  onDeleteItems(): void {
    // Not supported on this component.
  }

  /**
   * Clear the input filter.
   */
  private onClearFilter(): void {
    if (this.filterValue.length < 3) {
      this.filterValue = '';
      return;
    } else {
      this.filterValue = '';
      this.products = [];
    }
  }

  /**
   * Executes filter when user type some value.
   */
  onFilterData($event): void {
    if ($event.code === KEYSTROKE.ENTER) {
      this.searchModel.filterModel.model = this.filterValue;
      this.onSearch(true);
    } else if (!PREVENT_KEYUP_EVENTS.test($event.code)) {
      clearTimeout(this.typingTimeout);
      this.typingTimeout = setTimeout(() => {
        if (this.filterValue.length > 2 || this.filterValue === '') {
          this.searchModel.filterModel.model = this.filterValue;
          this.onSearch(true);
        }
      }, SEARCH_QUITE_PERIOD);
    }
  }

  /**
   * This function will handle the text input by the user on the Make combobox.
   * @param textInput
   */
  handleFilter(textInput: string): void {
    this.filteredMakes = this.productMakes.filter(
      (make: MakeModel) =>
        make.name.toLowerCase().indexOf(textInput.toLowerCase()) !== -1
    );
  }

  /**
   * When selected Make filter change, do search.
   */
  protected onMakeSelectedChange(event): void {
    if (this.makeFilterSelected) {
      this.searchModel.filterModel.make = this.makeFilterSelected.name;
    } else {
      this.searchModel.filterModel.make = null;
      this.handleFilter('');
    }
    if (event === undefined || event.hasOwnProperty('name') || event === '') {
      this.onSearch(true);
    } else if (typeof event === 'string') {
      this.products = [];
    }
  }

  /**
   * When select any particular row in grid.
   */
  protected onSelectItemChange(item: ProductModel): void {
    this.productSelected[0] = item[1] !== undefined ? item[1] : item[0];
  }

  /**
   * Infinity scrolling pagination functionality
   */
  infinityScroll(): void {
    if (this.hasNextPage) {
      this.onSearch();
    }
  }
}
